import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, Plus } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  averageWeeklyHours,
  CADENCE_GROUPS,
  landsThisWeek,
  taskWeeklyTotal,
  weekLoadHours,
} from "@/lib/hub";
import { round, type Task } from "@/components/work/types";
import { useWorkHub } from "@/components/work/useWorkHub";
import { CommitmentGroups, FilterChip, LensPresets } from "@/components/work/parts";
import { lensSummary } from "@/lib/lens";

import { FollowUpDialog, TaskDialog } from "@/components/work/TaskDialog";
import { CompleteDialog } from "@/components/work/CompleteDialog";
import { AllocateDialog } from "@/components/work/AllocateDialog";
import { TagFilters } from "@/components/work/TagFilters";

export const Route = createFileRoute("/_authenticated/work/regular-meetings")({
  head: () => ({
    meta: [
      { title: "Regular Meetings — MyPriorities" },
      {
        name: "description",
        content:
          "Every standing weekly, monthly and quarterly commitment in one place, with the hours they take each week.",
      },
      { property: "og:title", content: "Regular Meetings — MyPriorities" },
      {
        property: "og:description",
        content: "Your recurring meetings and routines, grouped by cadence.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: RegularMeetingsPage,
});

function RegularMeetingsPage() {
  const hub = useWorkHub();
  const [allocating, setAllocating] = useState<Task | null>(null);
  const { plan, recurringTasks, doneRecurring, totals, capacity, filter, setFilter, openTasks } =
    hub;
  const hiddenByWeek =
    hub.lens.mode !== "all"
      ? openTasks.filter((t) => t.recurrence !== "none").length - recurringTasks.length
      : 0;

  const share = capacity ? Math.round((totals.committedAvgWeek / capacity) * 100) : 0;

  return (
    <AppShell
      title="Regular Meetings"
      subtitle={`Standing commitments — ${round(totals.committedAvgWeek)}h a week on average (about ${share}% of your capacity), ${round(totals.committed)}h landing this week.`}
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to="/work">
              <ArrowLeft className="size-4" /> Work hub
            </Link>
          </Button>
          <Button onClick={() => hub.openCreate({ recurrence: "weekly", task_type: "Meeting" })}>
            <Plus className="size-4" /> New regular task
          </Button>
        </div>
      }
    >
      <section className="panel p-6">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {CADENCE_GROUPS.map((group) => {
            const isDaily = group.key === "day";
            const items = recurringTasks.filter((t) => group.values.includes(t.recurrence));
            const perOccurrence = items.reduce((s, t) => s + taskWeeklyTotal(t), 0);
            const average = items.reduce((s, t) => s + averageWeeklyHours(t), 0);
            const thisWeek = items.filter(landsThisWeek).reduce((s, t) => s + weekLoadHours(t), 0);
            const startsLater = items.filter((t) => !landsThisWeek(t)).length;
            return (
              <div key={group.key} className="rounded-md border border-border bg-surface p-4">
                <p className="label-caps">{group.title}</p>
                <p className="mt-2 font-display text-2xl font-bold text-primary">
                  {round(average)}h avg / week
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {items.length} open commitment{items.length === 1 ? "" : "s"} ·{" "}
                  {round(perOccurrence)}h {isDaily ? "per day" : "per occurrence"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {round(thisWeek)}h actually lands this week · {items.filter(landsThisWeek).length}{" "}
                  start
                  {items.filter(landsThisWeek).length === 1 ? "s" : ""} this week
                  {startsLater ? ` · ${startsLater} starts later` : ""}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-baseline justify-between gap-2 rounded-md border border-border bg-surface p-4">
          <div>
            <p className="label-caps">All standing commitments</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Monthly items spread over 4.33 weeks, quarterly over 13 weeks.
            </p>
          </div>
          <p className="font-display text-2xl font-bold text-primary">
            {round(totals.committedAvgWeek)}h avg / week
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {share}% of {round(capacity)}h capacity
            </span>
          </p>
        </div>
      </section>

      <div className="mt-6 space-y-2">
        <LensPresets
          lens={hub.lens}
          lenses={hub.lenses}
          onSelect={hub.selectLens}
          onSave={hub.savePreset}
          onDelete={hub.deletePreset}
          hiddenCount={hiddenByWeek}
        />
        <p className="text-xs text-muted-foreground">{lensSummary(hub.lens)}</p>
      </div>

      {plan.pillars.length ? (
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <span className="label-caps mr-1">Focus</span>
          <FilterChip
            active={filter.kind === "all"}
            onClick={() => setFilter({ kind: "all", id: null })}
          >
            Everything
          </FilterChip>
          {plan.pillars.map((p) => (
            <FilterChip
              key={p.id}
              active={filter.kind === "pillar" && filter.id === p.id}
              onClick={() => setFilter({ kind: "pillar", id: p.id })}
            >
              {p.name}
            </FilterChip>
          ))}
          {plan.goals.map((g) => (
            <FilterChip
              key={g.id}
              active={filter.kind === "goal" && filter.id === g.id}
              onClick={() => setFilter({ kind: "goal", id: g.id })}
            >
              ↳ {g.name}
            </FilterChip>
          ))}
        </div>
      ) : null}

      <TagFilters tasks={recurringTasks} value={hub.tagFilters} onChange={hub.setTagFilters} />

      <section className="mt-8">
        {recurringTasks.length === 0 && doneRecurring.length === 0 ? (
          <p className="panel p-5 text-sm text-muted-foreground">
            No regular commitments yet — add one and it will appear here instead of the matrix.
          </p>
        ) : (
          <CommitmentGroups
            recurringTasks={recurringTasks}
            doneRecurring={doneRecurring}
            pillarName={plan.pillarName}
            goalName={plan.goalName}
            childCount={hub.childCount}
            onDone={(t) => hub.startComplete(t)}
            onEdit={hub.openEdit}
            onDuplicate={hub.openDuplicate}
            onDelete={(t) => hub.deleteTask.mutate(t.id)}
            onAllocate={setAllocating}
            className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3"
          />
        )}
      </section>

      <AllocateDialog task={allocating} onClose={() => setAllocating(null)} />
      <TaskDialog hub={hub} />
      <CompleteDialog hub={hub} />
      <FollowUpDialog hub={hub} />
    </AppShell>
  );
}
